import { gql } from "@apollo/client"
import { useQuery } from "@oysterhr/core"

const TERMINATION_FRAGMENT = gql`
  fragment OtherTerminationFragment on Termination {
    approver {
      avatarUrl
      id
      name
    }
    cancelReason
    cancelledAt
    cancelledBy {
      avatarUrl
      id
      name
    }
    initiatedAt
    id
    lastPayment
    lastPaymentAmount
    lastPaymentCurrencyCode
    note
    onLeave
    proposedDate
    reason
    ptoAccurate
    ptoInaccurateNote
    ptoDaysRemaining
    roeCode
    notifyCustomerAdmin
    deductNegativePto
    payrollPayoutMonthChange
    proposedPayoutMonth
    payrollPayoutMonthReason
    calculatedPayoutMonth
    payrollSpecialistNotes {
      postingContext
    }
    requestor {
      avatarUrl
      id
      isOysterStaff
      name
      type
    }
    offboardingSpecialist {
      id
      name
      avatarUrl
      email
    }
    state
    primaryCategory {
      id
      name
    }
    secondaryCategory {
      id
      name
    }
    communicatedWithEmployee
    intendedDaysOff
    notes
    ptoInaccurateNote
    ptoDaysRemaining
    ptoAccurate
    documents {
      id
      title
      date
      tags
      filename
      createdAt
      description
    }
    additionalTerminationPayments {
      id
      allowance
      amount
      amountType
      currency
      period
      periodDuration
      amountAsPercentage
      paymentFrequency
      details
      period
      periodDuration
      allowance
      redundancyPaymentType
      numRecurringPayments
      offboardingPayType {
        id
        name
      }
    }
    paidInFullCheckDate
    paidInFullStatus
    ptoInfo {
      daysRemaining
      daysTaken
      daysApproved
      leaveYear
      timeOffManagementEnabled
    }
    mutualTermination
    serviceAgreementSignature
    tosAgreementGenerated
    additionalNotesForDepositTeam
    staffDepositNote
    additionalConsiderationAmount {
      subunit
      subunitToUnit
      currencyCode
    }
    integrationName
    additionalConsiderationAmountToBeConfirmed
  }
`

export const OFFBOARDING_FLOW_QUERY = gql`
  query OffboardingFlowEngagement($engagementId: ID!) {
    engagement(engagementId: $engagementId) {
      id
      ptoDaysTaken
      createdAt
      updatedAt
      payrollCurrencyCode
      cutoffMonthDay
      countryCode
      costOfOffboardingAvailable
      probationEnd
      initialStartDate
      isPayrollTeamMember
      lastDayOn
      lastWorkingDay
      pendingEngagementChange {
        compensationChanged
        applicableFrom
      }
      terminationDraft {
        id
        proposedDate
        reason
        communicatedWithEmployee
        intendedDaysOff
        ptoAccurate
        ptoInaccurateNote
        ptoDaysRemaining
        notes
        onLeave
        earlyOffboardingRequested
        earlyOffboardingReason
        teamMemberOwedCommission
        commissionDetails
        mutualTermination
        notifyCustomerAdmin
        roeCode
        deductNegativePto
        payrollPayoutMonthChange
        proposedPayoutMonth
        payrollPayoutMonthReason
        calculatedPayoutMonth
        documents {
          id
          title
          date
          tags
          filename
          createdAt
          description
        }
        primaryCategory {
          id
          public
        }
        secondaryCategory {
          id
        }
        additionalTerminationPayments {
          id
          amount
          currency
          amountAsPercentage
          paymentFrequency
          details
          period
          periodDuration
          allowance
          redundancyPaymentType
          numRecurringPayments
          offboardingPayType {
            id
            name
          }
        }
        costOfOffboarding {
          total
          monthlySalary
          proratedFinalMonthSalary
          mandatoryCosts {
            total
            noticePayment
            severancePayment
            holidayEntitlement
          }
          additionalCosts {
            total
            bonus
            commission
            expenses
          }
          allowancesAndBenefits {
            total
            workFromHome
            travel
            sustenance
            healthAndWellbeing
            healthInsurance
          }
          currency
        }
        ptoInfo {
          daysRemaining
          daysTaken
          daysApproved
          leaveYear
          timeOffManagementEnabled
        }
      }
      teamMember {
        id
        name
      }
      termination {
        ...OtherTerminationFragment
      }
      teamMemberName
      employment {
        id
        salaryAmount
        salaryCurrencyCode
        personalInfo {
          fullLegalName
        }
      }
      globalPayrollOffboardingCountry {
        currency
        pearlSupported
      }
      contract {
        id
        daysOfNotice
      }
      cancelledTerminations {
        ...OtherTerminationFragment
      }
    }
  }
  ${TERMINATION_FRAGMENT}
`

export const useEngagement = (engagementId: string) =>
  useQuery(OFFBOARDING_FLOW_QUERY, { variables: { engagementId }, fetchPolicy: "network-only" })
